import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Sparkles, PartyPopper, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { WA_LINK } from "../lib/constants";

const COUPON_IMAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_skill-success-hub/artifacts/24k2pv90_Early%20applicant%20September2026.jpeg";

// Deterministic-ish confetti pieces so re-renders are stable
const CONFETTI_PIECES = Array.from({ length: 26 }).map((_, i) => {
  const colors = ["#00B37E", "#0A66C2", "#F59E0B", "#FDE68A", "#22C55E", "#3B82F6"];
  const seed = (i * 9301 + 49297) % 233280;
  const rand = (n) => ((seed * (n + 1)) % 100) / 100;
  return {
    id: i,
    left: rand(1) * 100,
    delay: rand(2) * 1.2,
    duration: 2.4 + rand(3) * 1.6,
    color: colors[i % colors.length],
    rotate: rand(4) * 360,
    size: 6 + Math.floor(rand(5) * 6),
    shape: i % 3, // 0=square, 1=circle, 2=strip
  };
});

const SPARKLE_POINTS = [
  { top: "8%", left: "6%", size: 22, delay: 0 },
  { top: "14%", right: "8%", size: 18, delay: 0.4 },
  { bottom: "22%", left: "10%", size: 16, delay: 0.8 },
  { bottom: "10%", right: "12%", size: 20, delay: 0.2 },
  { top: "42%", left: "3%", size: 14, delay: 1.0 },
  { top: "38%", right: "4%", size: 14, delay: 0.6 },
];

export default function LaunchCelebrationPopup() {
  const [open, setOpen] = useState(false);

  // Fresh page load → show once, ~600ms after mount so hero paints first
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 sm:px-6"
          data-testid="launch-popup-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="launch-popup-title"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close launch offer"
            onClick={close}
            data-testid="launch-popup-backdrop"
            className="absolute inset-0 bg-[#0A192F]/60 backdrop-blur-[6px] cursor-default"
          />

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 10 }}
            transition={{ type: "spring", damping: 20, stiffness: 260 }}
            className="relative w-full max-w-lg sm:max-w-xl"
            data-testid="launch-popup-card"
          >
            {/* Animated brand glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-1 rounded-[28px] opacity-90 blur-[14px] launch-glow"
              style={{
                background:
                  "conic-gradient(from 0deg, #00B37E, #0A66C2, #00B37E, #F59E0B, #00B37E)",
              }}
            />

            <div className="relative rounded-[24px] bg-white shadow-2xl overflow-hidden ring-1 ring-white/60">
              {/* Confetti field (inside card, above image) */}
              <div className="absolute inset-x-0 top-0 h-full overflow-hidden pointer-events-none">
                {CONFETTI_PIECES.map((c) => (
                  <span
                    key={c.id}
                    className="absolute -top-6 launch-confetti"
                    style={{
                      left: `${c.left}%`,
                      width: c.shape === 2 ? c.size * 0.4 : c.size,
                      height: c.shape === 2 ? c.size * 1.4 : c.size,
                      background: c.color,
                      borderRadius:
                        c.shape === 1 ? "9999px" : c.shape === 2 ? "2px" : "3px",
                      animationDelay: `${c.delay}s`,
                      animationDuration: `${c.duration}s`,
                      transform: `rotate(${c.rotate}deg)`,
                      opacity: 0.9,
                    }}
                  />
                ))}
              </div>

              {/* Sparkles */}
              {SPARKLE_POINTS.map((s, i) => (
                <Sparkles
                  key={i}
                  aria-hidden="true"
                  className="absolute text-amber-400 launch-sparkle pointer-events-none"
                  style={{
                    top: s.top,
                    left: s.left,
                    right: s.right,
                    bottom: s.bottom,
                    width: s.size,
                    height: s.size,
                    animationDelay: `${s.delay}s`,
                    filter: "drop-shadow(0 0 6px rgba(251, 191, 36, 0.65))",
                  }}
                />
              ))}

              {/* Close button */}
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                data-testid="launch-popup-close"
                className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/95 hover:bg-white text-[#0A192F] shadow-md flex items-center justify-center transition-transform hover:scale-105"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Coupon image */}
              <div className="relative bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-4 pt-5 sm:px-6 sm:pt-6">
                <img
                  src={COUPON_IMAGE}
                  alt="TASSEI.in launch celebration — Flat 50% discount coupon"
                  data-testid="launch-popup-coupon"
                  className="w-full h-auto rounded-xl shadow-lg ring-1 ring-black/5"
                  loading="eager"
                />
              </div>

              {/* Body */}
              <div className="relative px-5 sm:px-8 pt-5 sm:pt-6 pb-6 sm:pb-8">
                <div className="flex items-center justify-center gap-2 text-emerald-600">
                  <PartyPopper className="w-5 h-5" />
                  <span className="text-[11px] sm:text-xs tracking-[0.22em] font-black uppercase">
                    Launch Celebration
                  </span>
                  <PartyPopper className="w-5 h-5" />
                </div>

                <h2
                  id="launch-popup-title"
                  className="mt-2 text-center font-heading text-2xl sm:text-3xl font-extrabold text-[#0A192F] leading-tight"
                >
                  <span aria-hidden="true" className="mr-1">🎉</span>
                  TASSEI.in is <span className="text-[#00B37E]">Now Live!</span>
                  <span aria-hidden="true" className="ml-1">🎉</span>
                </h2>
                <p className="mt-1.5 text-center text-sm sm:text-base font-semibold text-[#0A66C2]">
                  We&apos;re Celebrating Our Launch!
                </p>

                {/* Flat 50% OFF hero band */}
                <div className="relative mt-5 mx-auto max-w-[92%]">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-0.5 rounded-2xl launch-band-glow"
                    style={{
                      background:
                        "linear-gradient(90deg, #00B37E, #0A66C2, #00B37E)",
                    }}
                  />
                  <div className="relative rounded-2xl bg-[#0A192F] text-white px-4 py-3.5 sm:px-6 sm:py-4 flex items-center justify-center gap-2 sm:gap-3">
                    <Sparkles className="w-5 h-5 text-amber-300" />
                    <span className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                      FLAT{" "}
                      <span className="bg-gradient-to-r from-emerald-300 via-white to-sky-300 bg-clip-text text-transparent">
                        50% OFF
                      </span>
                    </span>
                    <Sparkles className="w-5 h-5 text-amber-300" />
                  </div>
                </div>

                <p className="mt-4 text-center text-[13px] sm:text-sm text-[#475569] leading-relaxed max-w-md mx-auto">
                  As part of our launch celebration, you are eligible to get a{" "}
                  <span className="font-bold text-[#0A192F]">FLAT 50% DISCOUNT</span> on
                  eligible TASSEI programs.
                  <span className="block mt-1 text-[12px] sm:text-[13px] text-[#64748B]">
                    Don&apos;t miss this launch-exclusive opportunity!
                  </span>
                </p>

                {/* CTAs */}
                <div className="mt-5 flex flex-col sm:flex-row gap-2.5 items-stretch justify-center">
                  <Link
                    to="/contact"
                    onClick={close}
                    data-testid="launch-popup-claim-cta"
                    className="flex-1"
                  >
                    <Button
                      className="w-full h-12 rounded-full bg-gradient-to-r from-[#00B37E] to-[#009166] hover:from-[#009166] hover:to-[#007a55] text-white font-black tracking-wide shadow-lg shadow-emerald-500/30"
                    >
                      CLAIM 50% DISCOUNT
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </Link>
                  <a
                    href={WA_LINK(
                      "Hi TASSEI, I want to claim the launch Flat 50% discount offer. Please share eligibility & next steps."
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                    data-testid="launch-popup-whatsapp-cta"
                    className="sm:w-auto"
                  >
                    <Button
                      variant="outline"
                      className="w-full h-12 rounded-full border-2 border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white font-bold px-5"
                    >
                      <MessageCircle className="w-4 h-4 mr-1.5" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="mt-3 text-center text-[11.5px] sm:text-xs text-[#64748B] flex items-center justify-center gap-1.5">
                  <span aria-hidden="true">📞</span>
                  Contact us now to check your eligibility &amp; claim your offer
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
